import type { CdnListResponse, CdnItem } from "@/types";

import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import { apiClient } from "@/utils/api";
import { buildApiParams } from "@/utils/helpers";
import { createPaginationParams, createTableQueryOptions } from "@/components/table";

// API Base Path
const CDN_BASE = '/cdns';

// Shared params atom for CDN list
export type CdnListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  refresh?: number;
};

export const cdnListParamsAtom = atom<CdnListParams>({
  ...createPaginationParams(),
  filters: {},
});

// Hydration flag to delay initial query until URL/filters are synced
export const cdnHydratedAtom = atom<boolean>(false);

// GET /cdns list query
export const cdnQueryAtom = atomWithQuery<CdnListResponse>((get) => {
  const params = get(cdnListParamsAtom);
  const hydrated = get(cdnHydratedAtom);

  return createTableQueryOptions({
    queryKey: ["cdns", params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        { page: params.page, per_page: params.per_page },
        params.filters || {}
      );

      const { data } = await apiClient.get(CDN_BASE, { params: apiParams });

      return data as CdnListResponse;
    },
    enabled: hydrated
  });
});

// CRUD Operations
export interface CdnForm {
  name: string;
  cdn_tag: 'stable' | 'latest' | 'exact' | 'dev' | 'beta';
  version: string;
  cdn_provider_id: number;
  filename: string;
  cdn_url: string;
}

// Create CDN
export const createCdn = atom(
  null,
  async (get, set, formData: CdnForm) => {
    const payload = {
      name: formData.name || null,
      cdn_tag: formData.cdn_tag || null,
      cdn_provider_id: formData.cdn_provider_id,
      version: formData.version || null,
      filename: formData.filename || null,
      cdn_url: formData.cdn_url || null
    };

    await apiClient.post(CDN_BASE, payload);

    // Trigger refresh by updating params
    const currentParams = get(cdnListParamsAtom);
    set(cdnListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Update CDN
export const updateCdn = atom(
  null,
  async (get, set, { id, formData }: { id: number; formData: CdnForm }) => {
    const payload = {
      name: formData.name || null,
      cdn_tag: formData.cdn_tag || null,
      cdn_provider_id: formData.cdn_provider_id,
      version: formData.version || null,
      filename: formData.filename || null,
      cdn_url: formData.cdn_url || null
    };

    await apiClient.put(`${CDN_BASE}/${id}`, payload);

    // Trigger refresh by updating params
    const currentParams = get(cdnListParamsAtom);
    set(cdnListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Delete CDN
export const deleteCdn = atom(
  null,
  async (get, set, id: number) => {
    await apiClient.delete(`${CDN_BASE}/${id}`);

    // Trigger refresh by updating params
    const currentParams = get(cdnListParamsAtom);
    set(cdnListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Set Default CDN
export const setDefaultCdn = atom(
  null,
  async (get, set, cdnId: number) => {
    await apiClient.post(`${CDN_BASE}/${cdnId}/set-default`);

    // Trigger refresh by updating params
    const currentParams = get(cdnListParamsAtom);
    set(cdnListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);
